import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {QuotesClient} from '../../../infrastracture/api/QuotesClient'
import {
  collectChildSystemFeeChargeRows,
  collectParentWorkOrderChargeRows,
  filterPendingCharges,
  getChildWorkOrderChargesForQuote,
  quoteLineTotalForQuote,
  sumChargeValues,
} from '../utils/quoteAmountsWithCharges'
import {fetchParentWorkOrderQuotesBundle} from '../utils/parentWorkOrderQuotesBundle'
import QuoteProjectChargesBreakdown from './QuoteProjectChargesBreakdown'
import {TableHeader} from '../../../common/components/TableHeader'
import Pagination from 'react-responsive-pagination'
import RecordPerPageSelector from '../../../common/components/selectors/RecordPerPageSelector'
import {useSelector, shallowEqual} from 'react-redux'
import {RootState} from '../../../../setup'
import {TenantTypeIds} from '../../../common/constants/tenantTypeIds'
import {toMoneyFormat} from '../../../common/helpers/stringHelper'

interface Props {
  workOrderId: string
  workOrderNumber: string
  title?: any
  refreshWO: () => void
  refreshData: () => void
  refreshList?: boolean
}
const QuotesForParentWorkOrderList: React.FC<Props> = (props) => {
  const {workOrderId, refreshList, workOrderNumber} = props
  const [Quotes, setQuotes] = useState<Array<any>>([])
  const [search, setSearch] = useState('')

  const user: any = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as any
  const tenantTypeId = user?.tenant?.tenantTypeId?.toUpperCase()
  const isContractor = tenantTypeId === TenantTypeIds.Contractor.toUpperCase()

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalRecords: 1,
    totalPages: 1,
    skip: 0,
    take: 50,
    reset: true,
    sort: 'name',
    direction: 'desc',
  })

  const [loading, setLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [expandedStatusId, setExpandedStatusId] = useState<string | null>(null)
  const [allFullQuotes, setAllFullQuotes] = useState<any[]>([])

  useEffect(() => {
    setExpandedStatusId(null)
  }, [workOrderId])

  useEffect(() => {
    fetchData()
  }, [workOrderId, refreshList])

  useEffect(() => {
    if (pagination.reset) fetchData()
  }, [search, pagination])

  async function fetchData() {
    setLoading(true)

    try {
      const bundle = await fetchParentWorkOrderQuotesBundle(workOrderId)

      if (bundle.successful) {
        const {groups, fullQuotes, chargesList, listPayload: data} = bundle

        setAllFullQuotes(fullQuotes)

        const enrichedGroups = groups.map((group: any) => {
          const statusName = group.quoteStatus?.name
          const quotesForStatus = fullQuotes.filter((q: any) => q.quoteStatus?.name === statusName)
          const linesSum = quotesForStatus.reduce(
            (sum: number, quote: any) => sum + quoteLineTotalForQuote(quote, isContractor),
            0
          )
          const statusKey = (statusName || '').trim().toLowerCase()
          let calculatedTotal = linesSum
          if (statusKey === 'approved') {
            const parentSubtotal = collectParentWorkOrderChargeRows(
              quotesForStatus,
              chargesList,
              workOrderId
            ).reduce((s, row) => s + (Number(row.charge?.value) || 0), 0)
            const childSystemSubtotal = collectChildSystemFeeChargeRows(
              quotesForStatus,
              chargesList,
              workOrderId
            ).reduce((s, row) => s + (Number(row.charge?.value) || 0), 0)
            const childChargesSum = quotesForStatus.reduce(
              (sum: number, quote: any) =>
                sum +
                sumChargeValues(getChildWorkOrderChargesForQuote(quote, chargesList, workOrderId)),
              0
            )
            calculatedTotal = linesSum + childChargesSum + parentSubtotal + childSystemSubtotal
          } else if (statusKey === 'submitted') {
            const pendingPool = sumChargeValues(filterPendingCharges(chargesList, workOrderId))
            calculatedTotal = linesSum + pendingPool
          }
          return {...group, calculatedTotal}
        })

        setQuotes(enrichedGroups)

        const totalPages = Math.ceil(data.total / pagination.take)

        setPagination({
          ...pagination,
          totalPages,
          totalRecords: data.total,
          reset: false,
        })
      } else {
        setAllFullQuotes([])
      }
      setLoading(false)
    } catch (e) {
      console.error(e)
      setAllFullQuotes([])
      setLoading(false)
    }
  }

  const getQuoteTotal = (quote: any) => quoteLineTotalForQuote(quote, isContractor)

  const getChildQuotes = (statusName: string) => {
    return allFullQuotes.filter((q: any) => q.quoteStatus?.name === statusName)
  }

  /** Groups from the API have no `id`; `quoteStatusId` is unique per status row. */
  const expandRowKey = (item: any) =>
    String(item.quoteStatusId ?? item.quoteStatus?.id ?? item.quoteStatus?.name ?? '')

  const childProjectLines = (quote: any) => {
    const wo = quote?.workOrder
    if (!wo) return {title: '—', subtitle: '' as string}
    const title =
      wo.workOrderNumber ||
      (wo.workOrderNumberId != null && wo.workOrderNumberId !== ''
        ? `WO-${wo.workOrderNumberId}`
        : null) ||
      '—'
    const subtitleParts = [wo.site?.name, wo.description].filter(
      (x: any) => x != null && String(x).trim() !== ''
    )
    const subtitle = subtitleParts.join(' · ')
    return {title: String(title), subtitle}
  }

  return (
    <>
      <div className={`card`}>
        <div className='card-header'>
          <div className='card-title'>
            <div className='d-flex align-items-center position-relative my-1'>
              <span className='svg-icon svg-icon-1 position-absolute ms-6'>
                <img src={toAbsoluteUrl('/media/icons/duotune/general/gen004.svg')} />
              </span>
              <input
                type='text'
                data-kt-notice-type-table-filter='search'
                className='form-control form-control-solid w-250px ps-14'
                placeholder='Search Quotes'
                onChange={(e) => {
                  setSearch(e.target.value)
                }}
              />
              {loading && (
                <div className='d-flex align-items-center'>
                  <span className='spinner-border spinner-border-lg align-middle ms-2'></span>
                </div>
              )}
            </div>
          </div>

          <div className='card-toolbar'>
            <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
              <Link
                className='btn btn-sm btn-light-primary me-2'
                to={`/quotes/summary/${workOrderId}`}
              >
                Quote Summary
              </Link>
              <button
                className='btn btn-sm btn-light me-2'
                disabled={loading || pdfLoading}
                onClick={async () => {
                  setPdfLoading(true)
                  try {
                    const response = await QuotesClient.DownloadQuoteSummaryPDF(workOrderId)
                    const blob = response.data
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    document.body.appendChild(a)
                    a.style.display = 'none'
                    a.href = url
                    const now = new Date()
                    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
                      2,
                      '0'
                    )}-${String(now.getDate()).padStart(2, '0')}`
                    a.download = `Quote Summary - ${workOrderNumber} - ${dateStr}.pdf`
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                  } catch (error) {
                    console.error('Failed to download PDF', error)
                  } finally {
                    setPdfLoading(false)
                  }
                }}
              >
                {pdfLoading ? (
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                ) : (
                  'Download PDF'
                )}
              </button>
              <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'></div>
            </div>
          </div>
        </div>
        <div className='card-body py-3'>
          <div className='table-responsive'>
            <table className='table align-middle gs-0 gy-4'>
              <TableHeader
                sort={pagination.sort}
                direction={pagination.direction}
                columns={[
                  {name: '', sort: '', static: true, width: '1'},
                  {name: 'Amount', sort: 'estimate', width: '10'},
                  {name: 'Project', sort: '', static: true, width: '22'},
                  {name: 'Status', sort: 'status', width: '10'},
                  {name: '', sort: '', static: true, width: '20'},
                ]}
                onUpdate={(sort: any, direction: any) => {
                  setPagination({
                    ...pagination,
                    sort,
                    direction,
                    reset: true,
                  })
                }}
              />
              {/* end::Table head */}
              {/* begin::Table body */}

              {!loading && (
                <tbody>
                  {!loading &&
                    Quotes.map((item) => {
                      const statusName = item.quoteStatus?.name || 'Submitted'
                      const rowKey = expandRowKey(item)
                      const isExpanded = expandedStatusId === rowKey
                      const childQuotes = isExpanded ? getChildQuotes(statusName) : []
                      const expandedStatusLower = (statusName || '').trim().toLowerCase()
                      const projectChargesSection: 'approved' | 'submitted' | null =
                        expandedStatusLower === 'approved'
                          ? 'approved'
                          : expandedStatusLower === 'submitted'
                            ? 'submitted'
                            : null
                      return (
                        <React.Fragment key={rowKey}>
                          <tr>
                            <td
                              className='pe-0 align-middle'
                              style={{width: '1%', whiteSpace: 'nowrap'}}
                            >
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-active-light-primary p-1'
                                onClick={() => setExpandedStatusId(isExpanded ? null : rowKey)}
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? 'Collapse quotes' : 'Expand quotes'}
                              >
                                {!isExpanded && (
                                  <KTSVG
                                    path='/media/icons/duotune/arrows/arr075.svg'
                                    className='svg-icon-2'
                                  />
                                )}
                                {isExpanded && (
                                  <KTSVG
                                    path='/media/icons/duotune/arrows/arr090.svg'
                                    className='svg-icon-2'
                                  />
                                )}
                              </button>
                            </td>
                            <td>
                              <span className='text-dark fw-bolder d-block mb-1 fs-6'>
                                {toMoneyFormat(item.calculatedTotal)}
                              </span>
                            </td>

                            <td className='text-muted fs-7'>—</td>

                            <td>
                              <div className='d-flex align-items-center rounded p-5'>
                                {item.quoteStatus?.color && (
                                  <span
                                    className={`bullet bullet-vertical h-30px `}
                                    style={
                                      item.quoteStatus?.color && {
                                        background: item.quoteStatus?.color,
                                      }
                                    }
                                  ></span>
                                )}{' '}
                                <div
                                  className='flex-grow-1 me-2'
                                  style={
                                    item.quoteStatus?.color && {
                                      paddingLeft: '5px',
                                    }
                                  }
                                >
                                  <span className='fw-bolder text-gray-800 fs-6'>
                                    {item.quoteStatus?.name}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td className='text-end'>
                              <Link
                                className='btn btn-bg-light btn-color-muted btn-active-color-primary btn-sm px-4 me-2'
                                to={`/quotes/summary/${workOrderId}?status=${encodeURIComponent(
                                  statusName
                                )}`}
                              >
                                Details
                              </Link>
                            </td>
                          </tr>
                          {/* {isExpanded && projectChargesSection != null && (
                            <QuoteProjectChargesBreakdown
                              quotesForStatus={childQuotes}
                              chargesList={chargesList}
                              parentWorkOrderId={workOrderId}
                              parentWorkOrderLabel={workOrderNumber || '—'}
                              statusSection={projectChargesSection}
                            />
                          )}
                          {isExpanded && childQuotes.length > 0 && (
                            <tr key={`${rowKey}-quotes-banner`} className='bg-light'>
                              <td
                                className='bg-light pe-0'
                                style={{width: '1%', whiteSpace: 'nowrap'}}
                              />
                              <td className='bg-light py-3' colSpan={4}>
                                <span className='fw-bold text-gray-700 fs-8 text-uppercase'>
                                  Quotes
                                </span>
                              </td>
                            </tr>
                          )} */}
                          {isExpanded &&
                            childQuotes.map((child: any) => {
                              const project = childProjectLines(child)
                              return (
                                <tr key={child.id} className='bg-light'>
                                  <td
                                    className='bg-light pe-0'
                                    style={{width: '1%', whiteSpace: 'nowrap'}}
                                  ></td>
                                  <td className='bg-light'>
                                    <span className='text-dark fw-bolder d-block mb-1 fs-6'>
                                      {toMoneyFormat(getQuoteTotal(child))}
                                    </span>
                                    {(child.name || child.description) && (
                                      <span className='text-muted fs-7 d-block'>
                                        {child.name || child.description}
                                      </span>
                                    )}
                                  </td>
                                  <td className='bg-light'>
                                    <span className='text-dark fw-bolder d-block fs-6'>
                                      {project.title}
                                    </span>
                                    {project.subtitle && (
                                      <span className='text-muted fs-7 d-block'>
                                        {project.subtitle}
                                      </span>
                                    )}
                                  </td>
                                  <td className='bg-light'>
                                    <div className='d-flex align-items-center rounded p-5'>
                                      {item.quoteStatus?.color && (
                                        <span
                                          className={`bullet bullet-vertical h-30px `}
                                          style={
                                            item.quoteStatus?.color && {
                                              background: item.quoteStatus?.color,
                                            }
                                          }
                                        ></span>
                                      )}{' '}
                                      <div
                                        className='flex-grow-1 me-2'
                                        style={
                                          item.quoteStatus?.color && {
                                            paddingLeft: '5px',
                                          }
                                        }
                                      >
                                        <span className='fw-bolder text-gray-800 fs-6'>
                                          {child.quoteStatus?.name || statusName}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className='text-end bg-light'>
                                    <Link
                                      className='btn btn-bg-light btn-color-muted btn-active-color-primary btn-sm px-4 me-2'
                                      to={`/quotes/summary/${workOrderId}?status=${encodeURIComponent(
                                        statusName
                                      )}&quoteId=${encodeURIComponent(child.id)}`}
                                    >
                                      Details
                                    </Link>
                                  </td>
                                </tr>
                              )
                            })}
                        </React.Fragment>
                      )
                    })}
                </tbody>
              )}
            </table>
          </div>

          <div className='card-footer px-1 py-4'>
            <div className='d-flex justify-content-between'>
              <div style={{minWidth: '200px'}}>
                <RecordPerPageSelector
                  placeholder='Rows per page'
                  onChange={(name, value) => {
                    setPagination({
                      ...pagination,
                      take: value,
                      reset: true,
                      currentPage: 1,
                    })
                  }}
                />
              </div>
              <div data-kt-notice-type-table-toolbar='base'>
                <Pagination
                  current={pagination.currentPage}
                  total={pagination.totalPages}
                  onPageChange={(page) => {
                    setPagination({
                      ...pagination,
                      currentPage: page,
                      skip: page === 1 ? 0 : (page - 1) * pagination.take,
                      reset: true,
                    })
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default QuotesForParentWorkOrderList
